/** Filtres de statut et de type. Fonctions pures — voir tests/filters.test.js. */

import { STATUSES, TYPES } from './constants.js'
import { hospitalStatuses } from './entities.js'
import { recordKey } from './record.js'

export const ALL_STATUSES = Object.freeze(
  Object.fromEntries(STATUSES.map((s) => [s, true])),
)
export const ALL_TYPES = Object.freeze(
  Object.fromEntries(TYPES.map((t) => [t, true])),
)

/** Le type d'affichage d'un établissement : GMF, GMF-U et hôpital ont chacun leur forme de marqueur. */
export function typeOf(establishment) {
  if (establishment.kind === 'hosp') return 'hosp'
  return establishment.type === 'GMF-U' ? 'gmfu' : 'gmf'
}

function isolate(filters, key) {
  return Object.fromEntries(Object.keys(filters).map((k) => [k, k === key]))
}

/**
 * Applique un filtre de statut.
 *
 * `isolateOnly` n'affiche que celui-là ; sinon on bascule sa présence.
 *
 * Le geste par défaut est l'isolement, et l'appelant le reflète : un clic nu
 * isole, Cmd/Ctrl-clic bascule (voir ui/sidebar.js). C'est l'inverse de la
 * convention Finder, et c'est voulu — on vient presque toujours ici pour
 * demander « montre-moi les clients », rarement pour retirer un statut d'un
 * ensemble.
 *
 * Tout décocher afficherait une carte vide sans que rien ne l'explique : on
 * réaffiche alors tout, ce qui est le seul état utile.
 */
export function toggleStatus(filters, status, isolateOnly = false) {
  if (isolateOnly) return isolate(filters, status)
  const next = { ...filters, [status]: !filters[status] }
  return Object.values(next).some(Boolean) ? next : { ...ALL_STATUSES }
}

/**
 * Applique un filtre de type. Même inversion que toggleStatus.
 *
 * Ne comporte volontairement pas le garde-fou « tout décocher réaffiche
 * tout » : masquer les trois types est ici une demande plausible (ne garder
 * que le fond de carte), et le clic suivant rétablit.
 */
export function toggleType(filters, type, isolateOnly = false) {
  if (isolateOnly) return isolate(filters, type)
  return { ...filters, [type]: !filters[type] }
}

/**
 * Les statuts qu'un établissement présente aux filtres.
 *
 * Un GMF n'en a qu'un. Un hôpital en a autant que de départements : avec
 * l'urgence en client et la pédiatrie en négociation, il est les deux, et doit
 * sortir sous les deux filtres. Le réduire à son statut le plus avancé le
 * ferait disparaître de « Négociations » alors qu'il y a bien une négociation
 * en cours dedans.
 *
 * Sans département, un hôpital est prospect : personne ne l'a travaillé.
 */
export function statusesOf(establishment, records) {
  if (establishment.kind === 'hosp') return hospitalStatuses(establishment.id, records)
  const status = records.get(recordKey(establishment.kind, establishment.id))?.status || 'prospect'
  return new Set([status])
}

/** true si l'établissement passe les deux filtres. */
export function isVisible(establishment, records, statusFilters, typeFilters) {
  if (!typeFilters[typeOf(establishment)]) return false
  for (const status of statusesOf(establishment, records)) {
    if (statusFilters[status]) return true
  }
  return false
}

/**
 * Établissements visibles, triés pour la colonne de résultats : par
 * municipalité puis par nom.
 *
 * localeCompare('fr-CA') et non `<` : une comparaison brute range les
 * municipalités accentuées après le Z (« Étoile » après « Zurich »), ce qui
 * les exile en fin de liste.
 */
export function visibleEstablishments(establishments, records, statusFilters, typeFilters) {
  return establishments
    .filter((est) => isVisible(est, records, statusFilters, typeFilters))
    .sort((a, b) => {
      const byMun = (a.mun || '').localeCompare(b.mun || '', 'fr-CA')
      return byMun !== 0 ? byMun : (a.nom || '').localeCompare(b.nom || '', 'fr-CA')
    })
}
